import React, { useState } from 'react';
import {
  FileCode,
  Brain,
  AlertTriangle,
  BarChart3,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronRight,
  Play,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  Eye,
  GitBranch,
  Shield,
  Clock,
  Zap,
  MessageSquare,
  Copy,
  Check
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

/**
 * StructuredResponse - Renders AI responses with structured blocks
 * Blocks: Files, Plan, Risk, Impact, Actions, Diff, Warning
 */
const StructuredResponse = ({
  response,
  onAction,
  disabled = false,
  taskStatus = 'planning'
}) => {
  const [expandedSections, setExpandedSections] = useState({
    files: true,
    plan: true,
    diff: false
  });
  const [planCopied, setPlanCopied] = useState(false);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const copyPlan = async (e) => {
    e.stopPropagation();
    if (!response.plan) return;
    
    const steps = Array.isArray(response.plan) ? response.plan : [response.plan];
    const formattedPlan = `Implementation Plan\n\n${steps.map((step, i) => `${i + 1}. ${step}`).join('\n')}`;
    
    try {
      await navigator.clipboard.writeText(formattedPlan);
      setPlanCopied(true);
      setTimeout(() => setPlanCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy plan:', err);
    }
  };

  const getRiskColor = (risk) => {
    const colors = {
      low: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
      medium: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      high: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      critical: 'bg-red-500/20 text-red-300 border-red-500/30'
    };
    return colors[risk?.toLowerCase()] || colors.medium;
  };

  const getRiskIcon = (risk) => {
    if (risk?.toLowerCase() === 'low') return <Shield className="w-4 h-4" />;
    if (risk?.toLowerCase() === 'critical') return <AlertTriangle className="w-4 h-4" />;
    return <AlertTriangle className="w-4 h-4" />;
  };

  return (
    <div className="space-y-4">
      {/* Summary Text */}
      {response.summary && (
        <p className="text-emerald-100 leading-relaxed">{response.summary}</p>
      )}

      {/* Files to Inspect */}
      {response.files && response.files.length > 0 && (
        <div className="rounded-xl bg-slate-800/30 border border-slate-700/50 overflow-hidden">
          <button
            onClick={() => toggleSection('files')}
            className="w-full flex items-center justify-between p-3 hover:bg-slate-800/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <FileCode className="w-4 h-4 text-blue-400" />
              <span className="text-sm font-medium text-white">Files to Inspect</span>
              <Badge className="bg-blue-500/20 text-blue-300 border-0 text-xs">
                {response.files.length}
              </Badge>
            </div>
            {expandedSections.files ? (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-400" />
            )}
          </button>
          {expandedSections.files && (
            <div className="px-3 pb-3 space-y-1">
              {response.files.map((file, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between p-2 rounded-lg bg-slate-900/50 text-xs"
                >
                  <span className="font-mono text-slate-300">{file.path || file}</span>
                  {file.lines && (
                    <span className="text-slate-500">{file.lines} lines</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Plan */}
      {response.plan && (
        <div className="rounded-2xl bg-emerald-950/40 border border-emerald-900/40 overflow-hidden">
          <div className="flex items-center justify-between p-3 hover:bg-emerald-900/20 transition-colors">
            <button
              onClick={() => toggleSection('plan')}
              className="flex items-center gap-2 flex-1"
            >
              <Brain className="w-4 h-4 text-purple-400" />
              <span className="text-sm font-medium text-white">Implementation Plan</span>
              {expandedSections.plan ? (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-slate-400" />
              )}
            </button>
            <button
              onClick={copyPlan}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all ${
                planCopied
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                  : 'bg-emerald-900/30 text-emerald-200 border border-emerald-800/40 hover:bg-emerald-800/40 hover:border-emerald-700/50'
              }`}
              data-testid="copy-plan-btn"
            >
              {planCopied ? (
                <>
                  <Check className="w-3.5 h-3.5" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-3.5 h-3.5" />
                  Copy
                </>
              )}
            </button>
          </div>
          {expandedSections.plan && (
            <div className="px-3 pb-3">
              {Array.isArray(response.plan) ? (
                <div className="space-y-2">
                  {response.plan.map((step, idx) => (
                    <div key={idx} className="flex items-start gap-3 text-sm">
                      <span className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-300 flex items-center justify-center text-xs font-medium flex-shrink-0">
                        {idx + 1}
                      </span>
                      <span className="text-slate-300 pt-0.5">{step}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-300">{response.plan}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Risk & Impact Row */}
      {(response.risk || response.impact) && (
        <div className="grid grid-cols-2 gap-3">
          {/* Risk Level */}
          {response.risk && (
            <div className="rounded-xl bg-slate-800/30 border border-slate-700/50 p-3">
              <div className="flex items-center gap-2 mb-2">
                {getRiskIcon(response.risk)}
                <span className="text-xs font-medium text-slate-400">Risk Level</span>
              </div>
              <Badge className={`${getRiskColor(response.risk)} border`}>
                {response.risk}
              </Badge>
              {response.riskReason && (
                <p className="text-xs text-slate-500 mt-2">{response.riskReason}</p>
              )}
            </div>
          )}

          {/* Impact */}
          {response.impact && (
            <div className="rounded-xl bg-slate-800/30 border border-slate-700/50 p-3">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-cyan-400" />
                <span className="text-xs font-medium text-slate-400">Impact</span>
              </div>
              <p className="text-sm text-slate-300">{response.impact}</p>
            </div>
          )}
        </div>
      )}

      {/* Token/Cost Estimate */}
      {response.estimate && (
        <div className="flex items-center gap-4 p-3 rounded-xl bg-slate-800/30 border border-slate-700/50">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-slate-400">Est. Tokens:</span>
            <span className="text-sm text-amber-300 font-medium">{response.estimate.tokens}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-400">Est. Cost:</span>
            <span className="text-sm text-emerald-300 font-medium">${response.estimate.cost?.toFixed(4)}</span>
          </div>
        </div>
      )}

      {/* Diff Preview */}
      {response.diff && (
        <div className="rounded-xl bg-slate-800/30 border border-slate-700/50 overflow-hidden">
          <button
            onClick={() => toggleSection('diff')}
            className="w-full flex items-center justify-between p-3 hover:bg-slate-800/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <GitBranch className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-medium text-white">Diff Preview</span>
            </div>
            {expandedSections.diff ? (
              <ChevronDown className="w-4 h-4 text-slate-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-400" />
            )}
          </button>
          {expandedSections.diff && (
            <div className="px-3 pb-3">
              {response.diff.map((file, idx) => (
                <div key={idx} className="mb-3 last:mb-0">
                  <div className="bg-slate-700/50 px-3 py-1.5 text-xs font-mono text-slate-400 rounded-t-lg">
                    {file.path}
                  </div>
                  <pre className="bg-slate-900/70 p-3 text-xs font-mono overflow-x-auto rounded-b-lg">
                    {file.changes?.split('\n').map((line, i) => {
                      let lineClass = 'text-slate-400';
                      if (line.startsWith('+')) lineClass = 'text-emerald-400';
                      if (line.startsWith('-')) lineClass = 'text-red-400';
                      if (line.startsWith('@@')) lineClass = 'text-cyan-400';
                      return (
                        <div key={i} className={lineClass}>
                          {line}
                        </div>
                      );
                    })}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Warning */}
      {response.warning && (
        <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-300">{response.warning}</p>
          </div>
        </div>
      )}

      {/* Approval Request */}
      {response.awaitingApproval && (
        <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-5 h-5 text-emerald-400" />
            <span className="font-medium text-emerald-300">
              {response.approvalType === 'plan' ? 'Plan Ready for Approval' :
               response.approvalType === 'patch' ? 'Patch Ready for Review' :
               response.approvalType === 'deploy' ? 'Ready to Deploy' :
               'Awaiting Approval'}
            </span>
          </div>
          <p className="text-sm text-emerald-200/80 mb-4">
            {response.approvalMessage || 'Review the changes above and approve to proceed.'}
          </p>
          <div className="flex flex-wrap gap-2">
            {response.approvalType === 'files' && (
              <Button
                onClick={() => onAction?.('approve_inspection')}
                disabled={disabled}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Eye className="w-4 h-4 mr-2" />
                Approve File Inspection
              </Button>
            )}
            {response.approvalType === 'plan' && (
              <>
                <Button
                  onClick={() => onAction?.('approve_plan')}
                  disabled={disabled}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <ThumbsUp className="w-4 h-4 mr-2" />
                  Approve Plan
                </Button>
                <Button
                  onClick={() => onAction?.('generate_patch')}
                  disabled={disabled || taskStatus !== 'approved'}
                  variant="outline"
                  className="border-blue-500/50 text-blue-300 hover:bg-blue-500/10"
                >
                  <GitBranch className="w-4 h-4 mr-2" />
                  Generate Patch
                </Button>
              </>
            )}
            {response.approvalType === 'patch' && (
              <>
                <Button
                  onClick={() => onAction?.('approve_deploy')}
                  disabled={disabled}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Approve & Deploy
                </Button>
                <Button
                  onClick={() => onAction?.('request_revision')}
                  disabled={disabled}
                  variant="outline"
                  className="border-purple-500/50 text-purple-300 hover:bg-purple-500/10"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Request Revision
                </Button>
              </>
            )}
            {response.approvalType === 'deploy' && taskStatus === 'executed' && (
              <Button
                onClick={() => onAction?.('rollback')}
                disabled={disabled}
                variant="outline"
                className="border-orange-500/50 text-orange-300 hover:bg-orange-500/10"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Rollback
              </Button>
            )}
            <Button
              onClick={() => onAction?.('reject')}
              disabled={disabled}
              variant="outline"
              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              <ThumbsDown className="w-4 h-4 mr-2" />
              Reject
            </Button>
          </div>
        </div>
      )}

      {/* Quick Actions (not awaiting approval) */}
      {response.quickActions && !response.awaitingApproval && (
        <div className="flex flex-wrap gap-2">
          {response.quickActions.map((action, idx) => (
            <Button
              key={idx}
              onClick={() => onAction?.(action.action)}
              disabled={disabled || action.disabled}
              variant="outline"
              size="sm"
              className="border-slate-600 text-slate-300 hover:border-emerald-500/50 hover:text-emerald-300"
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
};

export default StructuredResponse;
